import React, { useEffect, useState } from 'react';
import { Flex, Box, Text, Button, Icon, Link, Accordion, useToast, FormControl, Tooltip } from '@chakra-ui/react';
import { Link as WouterLink, useLocation } from 'wouter';
import { BsArrowLeft } from 'react-icons/bs';
import { FaLinkedin } from 'react-icons/fa';
import { FormProvider, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClosedCaptioning } from '@fortawesome/free-regular-svg-icons';
import { useDispatch, useTrackedState } from '../config/store';

import CandidateAccordion from '../components/interview/CandidateAccordion';
import JobOpeningAccordion from '../components/interview/JobOpeningAccordion';
import InterviewAccordion from '../components/interview/InterviewAccordion';
import RecommendationAccordion from '../components/interview/RecommendationAccordion';
import InterviewsError from '../components/interviews/InterviewsError';

function Interview({ params }) {
  const [_location, setLocation] = useLocation();
  const methods = useForm();
  const dispatch = useDispatch();
  const state = useTrackedState();
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  const [haveSubmitted, setHaveSubmitted] = useState(false);

  useEffect(() => {
    if (state.invalidInterview && !state.pending) {
      setLocation('/interviews/404');
    }
  }, [state.invalidInterview, state.pending]);

  // Get Metadata and single Interview, if user loggedin and loaded
  useEffect(() => {
    if (state && state.user) {
      if (!state.metadata) {
        dispatch({ type: 'GET_METADATA', token: state.user.token });
      }

      if (!state.interview || state.interview !== params.id) {
        dispatch({
          type: 'GET_INTERVIEW_AND_STORE',
          token: state.user.token,
          interviewId: params.id,
        });
      }
    }
  }, [state.user]);

  // Subscribe the component to the store
  useEffect(() => {}, [state.nonTechCheckbox]);

  // Handle submitting state, either redirect to Interviews list or show error
  useEffect(() => {
    // While submitting, set a 10s timeout to reset SUBMIT button
    if (submitting) {
      const submittingTimeout = setTimeout(() => {
        setSubmitting(false);
      }, 10000);
      return () => clearTimeout(submittingTimeout);
    }
    return undefined;
  }, [submitting]);

  useEffect(() => {
    if (state.interviewSubmitStatus) {
      setSubmitting(false);

      // If interviewSubmitStatus success, redirect, otherwise show the error
      if (state.interviewSubmitStatus === 'record updated') {
        const interviewId = state.interview.interviewData.id;
        dispatch({ type: 'TEMPORAL_LOCK_INTERVIEW', interviewId });
        setLocation('/interviews');
      } else if (state.interviewSubmitStatus === 'Fail Validation') {
        let errorMessageText = state.interviewSubmitValidation;
        // Remove trailing period if present
        if (errorMessageText.endsWith('.')) {
          errorMessageText = errorMessageText.slice(0, -1);
        }
        const errorMessages = errorMessageText.split('. ');
        errorMessages.forEach(errorMessage => {
          if (errorMessage) {
            toast({
              position: 'bottom-right',
              isClosable: true,
              title: 'Validation Error',
              description: `${errorMessage}.`,
              status: 'warning',
              duration: null,
              variant: 'left-accent',
            });
          }
        });
      } else {
        toast({
          position: 'bottom-right',
          isClosable: true,
          title: 'Unexpected Error',
          description: `Try again later or contact us at screeners-feedback@nextonlabs.com explaining what happened so that we can work on a fix!`,
          status: 'warning',
          duration: null,
          variant: 'left-accent',
        });
      }
    }
  }, [state.interviewSubmitStatus, state.interviewSubmitValidation]);

  // Reset / clear Interview data form
  useEffect(() => {
    if (state?.interview?.interviewData) {
      let sanitizeChecklistData = {};
      if (!state?.checklistItems?.checks) return;
      if (state?.interview?.interviewData?.Checklist_Items) {
        const resumeChecklist = JSON.parse(state?.checklistItems?.checks).filter(
          item => !item.internal && item.text !== '',
        );
        const requiredChecklist = resumeChecklist.filter(item => item.required);
        const notRequiredChecklist = resumeChecklist.filter(item => !item.required);
        const totalChecklist = [...requiredChecklist, ...notRequiredChecklist];
        sanitizeChecklistData = totalChecklist.map(item => {
          const checklistOnDB = state?.interview?.interviewData?.Checklist_Items?.find(check =>
            check.Item_Id ? check.Item_Id === item.id : check.Checklist_Item === item.text,
          );
          return {
            Item_Id: item.id,
            Checklist_Item: item.text,
            Screeners_Decision: checklistOnDB?.Screeners_Decision,
            Screeners_Comment: checklistOnDB?.Screeners_Comment,
          };
        });
      }
      methods.reset({ ...state.interview.interviewData, Checklist_Items: sanitizeChecklistData });
    }
  }, [state.interview, state?.checklistItems]);

  const dispatchInterviewSubmit = submitInterview => {
    dispatch({ type: 'UPDATE_TOKEN_AND_POST_INTERVIEW', submitInterview });
    setSubmitting(true);
    setHaveSubmitted(true);
  };

  // Curate and submit data
  const onSubmit = data => {
    const curatedData = { ...data };

    const expectedSalary = curatedData.Expected_monthly_salary_USD;
    const currentSalary = curatedData.Current_monthly_salary_USD;

    if ((currentSalary > 0 && currentSalary < 100) || currentSalary % 1 !== 0 || currentSalary < 0) {
      toast({
        position: 'bottom-right',
        isClosable: true,
        title: 'Current Salary Error',
        description: `Invalid value. The number must be equal or greater than 100 or 0.`,
        status: 'warning',
        duration: null,
        variant: 'left-accent',
      });
      return;
    }
    if ((expectedSalary > 0 && expectedSalary < 100) || expectedSalary % 1 !== 0 || expectedSalary < 0) {
      toast({
        position: 'bottom-right',
        isClosable: true,
        title: 'Expected Salary Error',
        description: `Invalid value. The number must be equal or greater than 100 or 0.`,
        status: 'warning',
        duration: null,
        variant: 'left-accent',
      });
      return;
    }

    const validationShadowingSession = curatedData.Shadow_Session && !curatedData.Trainees_Name;
    const validationNonTech =
      state.nonTechCheckbox && (!curatedData.Seniority_Level_By_Tech || !curatedData.Seniority_Level_By_Tech[0]);
    const validationBlockedReason =
      !curatedData.Interview_Blocked_Reason || curatedData.Interview_Blocked_Reason === '-None-';
    const validationEmptySeniorityLevel = curatedData.Seniority_Level_By_Tech?.find(
      item => item.Tech !== '' && item.Experience === '',
    );
    const validationEmptyItemSeniorityLevelByTech = curatedData.Seniority_Level_By_Tech?.find(
      item => (item.Tech === '' && item.Experience === '') || item.Tech === '',
    );

    const errorValidationMessage = (title, description) => {
      const toastMessage = () => {
        toast({
          position: 'bottom-right',
          isClosable: true,
          title: `${title} Error`,
          description: `Please complete the ${description}`,
          status: 'warning',
          duration: null,
          variant: 'left-accent',
        });
      };
      return { toastMessage };
    };

    const shadowingSessionToast = errorValidationMessage('Shadowing Session', `Shadow name`);
    const nonTechToast = errorValidationMessage(
      'Strongest Technology Stack',
      `Strongest Technology Stack or choose the non-tech position checkbox`,
    );
    const candidateGoalsToast = errorValidationMessage(`Candidate's goals`, `Candidate's goals`);
    const englishLevelToast = errorValidationMessage('English Level', `English Level`);
    const softSkillsToast = errorValidationMessage('Soft Skills Score', `Soft Skills Score`);
    const expectedSalaryToast = errorValidationMessage('Monthly Salary', `Expected Monthly Salary`);
    const nextonStatusToast = errorValidationMessage(`Candidate's Status`, `Candidate's Status`);
    const generalScoreToast = errorValidationMessage(`Job Opening Specific Score`, `Job Opening Specific Score`);
    const generalScore2Toast = errorValidationMessage(`General Score`, `General Score`);
    const mainRoleToast = errorValidationMessage(`Position and Seniority recommendation`, `Position recommendation`);
    const recommendedSeniorityToast = errorValidationMessage(
      `Position and Seniority recommendation`,
      `Seniority recommendation`,
    );
    const emptySeniorityLevelToast = errorValidationMessage(
      `Strongest Technology Stack`,
      `Experience level for all stacks`,
    );
    const emptySeniorityLevelByTechToast = errorValidationMessage(
      `Strongest Technology Stack`,
      `the rows with empty "Programming Language / Framework" or remove them`,
    );

    if (validationBlockedReason && validationShadowingSession) {
      shadowingSessionToast.toastMessage();
      return;
    }
    if (validationBlockedReason && validationNonTech && curatedData.Interview_Type !== 'Coaching Session') {
      nonTechToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.Candidate_goals) {
      candidateGoalsToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.English_level && curatedData.Interview_Type !== 'Coaching Session') {
      englishLevelToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.Soft_skills) {
      softSkillsToast.toastMessage();
      return;
    }
    if (
      validationBlockedReason &&
      !curatedData.Expected_monthly_salary_USD &&
      curatedData.Interview_Type !== 'Coaching Session'
    ) {
      expectedSalaryToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.Nexton_Status && curatedData.Interview_Type !== 'Coaching Session') {
      nextonStatusToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.General_Score && curatedData.Interview_Type !== 'Coaching Session') {
      generalScoreToast.toastMessage();
      return;
    }
    if (validationBlockedReason && !curatedData.General_Score2 && curatedData.Interview_Type !== 'Coaching Session') {
      generalScore2Toast.toastMessage();
      return;
    }
    if (
      validationBlockedReason &&
      !curatedData.Main_role?.length &&
      curatedData.Interview_Type !== 'Coaching Session'
    ) {
      mainRoleToast.toastMessage();
      return;
    }
    if (
      validationBlockedReason &&
      !curatedData.Recommended_Seniority?.length &&
      curatedData.Interview_Type !== 'Coaching Session'
    ) {
      recommendedSeniorityToast.toastMessage();
      return;
    }
    if (validationBlockedReason && validationEmptySeniorityLevel && curatedData.Interview_Type !== 'Coaching Session') {
      emptySeniorityLevelToast.toastMessage();
      return;
    }
    if (
      validationBlockedReason &&
      validationEmptyItemSeniorityLevelByTech &&
      curatedData.Interview_Type !== 'Coaching Session'
    ) {
      emptySeniorityLevelByTechToast.toastMessage();
      return;
    }

    Object.keys(curatedData).forEach(
      k => ((!curatedData[k] && curatedData[k] !== undefined) || curatedData[k] === '') && delete curatedData[k],
    );

    dispatchInterviewSubmit(curatedData);
  };

  // Alert user. Custom value/text is ignored, hence just ''
  const alertUser = event => {
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.returnValue = '';
  };
  const handleTabClosing = () => {}; // No action on unload for now

  // Listen for window when trying to close or reload this page, cleanup afterwards
  useEffect(() => {
    window.addEventListener('beforeunload', alertUser);
    window.addEventListener('unload', handleTabClosing);
    return () => {
      window.removeEventListener('beforeunload', alertUser);
      window.removeEventListener('unload', handleTabClosing);
    };
  });

  return (
    state.interview &&
    state.metadata &&
    state.experienceMetadata && (
      <Box maxW='1366px' px='20px' mt='24px' pb='32px'>
        <Flex justifyContent='space-between' alignItems='center' mb='20px'>
          <Flex alignItems='center'>
            <WouterLink href='/interviews'>
              <Button size='lg' colorScheme='gray' color='gray' px='17px'>
                <Icon as={BsArrowLeft} boxSize={6} />
              </Button>
            </WouterLink>
            <Text fontSize='32px' fontWeight='500' color='#337bff' letterSpacing='-1px' ml='42px' paddingRight='10px'>
              {state.interview.interviewData?.Candidate_Name?.name}
            </Text>
            {state?.interview?.interviewData.Interview_Type === 'Catch up' && (
              <Flex alignSelf='center'>
                <Tooltip
                  fontSize='sm'
                  fontWeight='400'
                  color='grey.900'
                  bg='white'
                  border='1px solid #D0D0D0'
                  boxShadow='0px 41px 60px 11px rgba(161, 161, 161, 0.160047)'
                  borderRadius='16px'
                  p='24px'
                  maxWidth='400px'
                  aria-label='A tooltip'
                  placement='bottom-start'
                  label='Catch up Call'>
                  <Icon boxSize={7}>
                    <FontAwesomeIcon icon={faClosedCaptioning} size='2xl' style={{ color: '#ff6d00' }} />
                  </Icon>
                </Tooltip>
              </Flex>
            )}
          </Flex>
          <Flex flexDirection='column' alignItems='center'>
            <Button variant='link' colorScheme='blue' marginBottom='15px' marginTop='15px'>
              <Link href={state.interview.candidateData?.Snapshot_LinkedIn_Profile} target='_blank'>
                <Flex alignItems='center'>
                  <Icon as={FaLinkedin} boxSize={5} />
                  <Text fontSize='22px' fontWeight='500' letterSpacing='-0.5px' ml='8px'>
                    {state.interview.candidateData?.Snapshot_LinkedIn_Profile}
                  </Text>
                </Flex>
              </Link>
            </Button>
          </Flex>
        </Flex>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <FormControl>
              <Accordion defaultIndex={[0]} allowMultiple>
                <CandidateAccordion interview={state.interview} />
                <JobOpeningAccordion interview={state.interview} />
                <InterviewAccordion
                  interview={state.interview}
                  metadata={state.metadata}
                  experienceMetadata={state.experienceMetadata}
                />
                <RecommendationAccordion interview={state.interview} metadata={state.metadata} />
              </Accordion>
              <Flex justifyContent='center'>
                {/* This disabled submit button prevents implicit form submission */}
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <button type='submit' disabled style={{ display: 'none' }} />

                <Button type='submit' isLoading={submitting} loadingText='SUBMITTING' size='lg' colorScheme='blue'>
                  SUBMIT
                </Button>
              </Flex>
            </FormControl>
          </form>
        </FormProvider>
        {state.error && haveSubmitted && <InterviewsError />}
      </Box>
    )
  );
}

export default Interview;
